import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Minimal Invoice PDF Test Page
 * Admin-only page for testing PDF generation
 * 
 * This is a temporary testing interface - not the full invoice dashboard.
 */
const InvoicePDFTestPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [invoiceInput, setInvoiceInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceList, setInvoiceList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [emailSending, setEmailSending] = useState({});
  const [emailResults, setEmailResults] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  // Show loading while auth is checking
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Check if user is admin
  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is for admin testing only.</p>
        <button onClick={() => navigate('/login')} style={{ padding: '10px 20px', cursor: 'pointer' }}>
          Go to Login
        </button>
      </div>
    );
  }

  const loadInvoiceList = async () => {
    setListLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/list?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.invoices) {
        setInvoiceList(data.invoices);
      }
    } catch (err) {
      setError('Failed to load invoices: ' + err.message);
    } finally {
      setListLoading(false);
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    setLoading(true);
    setError('');
    
    try {
      // Determine endpoint based on input
      let endpoint;
      if (invoiceId) {
        endpoint = `${API_URL}/api/admin/invoices/${invoiceId}/pdf`;
      } else if (invoiceNumber) {
        endpoint = `${API_URL}/api/admin/invoices/number/${invoiceNumber}/pdf`;
      } else {
        setError('Please enter an invoice ID or number');
        setLoading(false);
        return;
      }

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to download PDF');
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManualDownload = () => {
    const input = invoiceInput.trim();
    if (input.startsWith('invoice_')) {
      downloadPDF(input, null);
    } else if (input.startsWith('GAT-')) {
      downloadPDF(null, input);
    } else {
      setError('Enter invoice_id (starts with "invoice_") or invoice_number (starts with "GAT-")');
    }
  };

  const sendEmail = async (invoiceId, invoiceNumber, recipientType = 'both') => {
    const key = invoiceId || invoiceNumber;
    setEmailSending(prev => ({ ...prev, [key]: true }));
    setEmailResults(prev => ({ ...prev, [key]: null }));
    setError('');
    
    try {
      const endpoint = invoiceId 
        ? `${API_URL}/api/admin/invoices/${invoiceId}/send-email`
        : `${API_URL}/api/admin/invoices/number/${invoiceNumber}/send-email`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ recipient_type: recipientType })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send email');
      }

      setEmailResults(prev => ({ ...prev, [key]: data }));

    } catch (err) {
      setError(err.message);
      setEmailResults(prev => ({ ...prev, [key]: { status: 'failed', error: err.message } }));
    } finally {
      setEmailSending(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '40px auto', 
      padding: '20px',
      fontFamily: 'system-ui, sans-serif'
    }}>
      <h1 style={{ marginBottom: '10px' }}>Invoice PDF Test</h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Admin-only testing page for PDF invoice generation
      </p>

      {/* Manual Input Section */}
      <div style={{ 
        background: '#f5f5f5', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '30px'
      }}>
        <h3 style={{ marginTop: 0 }}>Download by Invoice ID or Number</h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <input
            type="text"
            value={invoiceInput}
            onChange={(e) => setInvoiceInput(e.target.value)}
            placeholder="invoice_xxx or GAT-xxxx-xxxxxxxx-xx"
            style={{ 
              flex: 1, 
              padding: '10px', 
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
            data-testid="invoice-input"
          />
          <button
            onClick={handleManualDownload}
            disabled={loading || !invoiceInput.trim()}
            style={{
              padding: '10px 20px',
              background: loading ? '#ccc' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
            data-testid="download-pdf-btn"
          >
            {loading ? 'Downloading...' : 'Download PDF'}
          </button>
        </div>
        {error && (
          <p style={{ color: '#dc2626', margin: '10px 0 0 0', fontSize: '14px' }}>
            {error}
          </p>
        )}
      </div>

      {/* Invoice List Section */}
      <div style={{ 
        background: '#f5f5f5', 
        padding: '20px', 
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>Existing Invoices</h3>
          <button
            onClick={loadInvoiceList}
            disabled={listLoading}
            style={{
              padding: '8px 16px',
              background: listLoading ? '#ccc' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: listLoading ? 'not-allowed' : 'pointer',
              fontSize: '13px'
            }}
            data-testid="load-invoices-btn"
          >
            {listLoading ? 'Loading...' : 'Load Invoices'}
          </button>
        </div>

        {invoiceList.length === 0 ? (
          <p style={{ color: '#666', fontSize: '14px' }}>
            Click "Load Invoices" to see existing invoices.
            <br/><br/>
            <strong>Note:</strong> If no invoices appear after loading, you need to generate an invoice first using the Phase 1 API.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#e5e5e5' }}>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ccc' }}>Invoice Number</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ccc' }}>Tutor</th>
                <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Total</th>
                <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ccc' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoiceList.map((inv) => {
                const emailResult = emailResults[inv.invoice_id];
                const isSending = emailSending[inv.invoice_id];
                
                return (
                  <React.Fragment key={inv.invoice_id}>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{inv.invoice_number}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#999' }}>{inv.invoice_id}</div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div>{inv.tutor_name_snapshot}</div>
                        <div style={{ fontSize: '11px', color: '#666' }}>{inv.tutor_email_snapshot}</div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>${inv.total_amount}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => downloadPDF(inv.invoice_id, inv.invoice_number)}
                            disabled={loading}
                            style={{
                              padding: '6px 12px',
                              background: '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            data-testid={`download-${inv.invoice_number}`}
                          >
                            PDF
                          </button>
                          <button
                            onClick={() => sendEmail(inv.invoice_id, inv.invoice_number, 'both')}
                            disabled={isSending}
                            style={{
                              padding: '6px 12px',
                              background: isSending ? '#ccc' : '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: isSending ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                            data-testid={`email-${inv.invoice_number}`}
                          >
                            {isSending ? 'Sending...' : 'Send Email'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {emailResult && (
                      <tr style={{ background: emailResult.status === 'success' ? '#ecfdf5' : '#fef2f2' }}>
                        <td colSpan="4" style={{ padding: '8px 8px 8px 20px', fontSize: '12px' }}>
                          <strong>Email Result:</strong>{' '}
                          {emailResult.status === 'success' ? (
                            <span style={{ color: '#059669' }}>
                              Tutor: {emailResult.tutor_email_sent ? 'Sent' : 'Failed'} | 
                              Admin: {emailResult.admin_email_sent ? 'Sent' : 'Failed'}
                            </span>
                          ) : (
                            <span style={{ color: '#dc2626' }}>{emailResult.error || 'Failed'}</span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Back to Admin */}
      <div style={{ marginTop: '30px', textAlign: 'center' }}>
        <button
          onClick={() => navigate('/admin')}
          style={{
            padding: '10px 20px',
            background: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Admin Dashboard
        </button>
      </div>
    </div>
  );
};

export default InvoicePDFTestPage;
